import React, { useState, ChangeEvent, KeyboardEvent, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

// store
import {
  selectIsNewRoomCreating,
  selectNewRoomCustomUrl,
  selectNewRoomHasCustomUrl,
  selectNewRoomName,
} from '../../../store/new-room/selectors';
import { createRoom, updateNewRoomData } from '../../../store/new-room/actions';

// components
import { HereInput, HereSwitch } from '../../../components/inputs';
import { ButtonContainer, CreateRoomButton } from '../shared-styles';

// utils
import { checkUrlAlias, ROOM_URL_ALIAS_VALID_MESSAGE } from '../../../../util/url-alias-validator';

const newRoomNameInputId = 'new-room-name-input-id';

const ROOM_MAX_NAME_LENGTH = 100;
const INPUT_URL_TIMER_DELAY_MS = 350;

let checkInputURLDelayTimer: ReturnType<typeof setTimeout> = null;

const RoomCreationNaming = () => {
  const dispatch = useDispatch();

  const [urlCheckMessage, setUrlCheckMessage] = useState(null);
  const [checkingUrl, setCheckingUrl] = useState(null);
  const [urlCheckResult, setUrlCheckResult] = useState(null);

  const newRoomName = useSelector(selectNewRoomName);
  const newRoomCustomUrl = useSelector(selectNewRoomCustomUrl);
  const isNewRoomCreating = useSelector(selectIsNewRoomCreating);
  const hasCustomUrl = useSelector(selectNewRoomHasCustomUrl);

  const blankNewRoomName = !newRoomName.trim();
  const newRoomNameExceedLength = newRoomName.length >= ROOM_MAX_NAME_LENGTH;
  const isValidRoomName = !blankNewRoomName && !newRoomNameExceedLength;
  const enableCreateRoomButton =
    !isNewRoomCreating && isValidRoomName && (!hasCustomUrl || urlCheckResult || urlCheckMessage === '');

  const onNewRoomNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch(updateNewRoomData({ data: { name: e.target.value } }));
  };

  const onCustomUrlSwitchToggle = (toggled: boolean) => {
    dispatch(updateNewRoomData({ data: { hasCustomUrl: toggled } }));
  };

  const onCustomUrlChange = (e: ChangeEvent<HTMLInputElement>) => {
    const lowercasedCustomUrl = e.target.value.toLowerCase();
    dispatch(updateNewRoomData({ data: { customUrl: lowercasedCustomUrl } }));
  };

  const onLockChange = (toggled: boolean) => {
    dispatch(updateNewRoomData({ data: { joinModeOpen: toggled } }));
  };

  const checkUrl = useCallback(async (message: string) => {
    const urlAliasErrorMessage = await checkUrlAlias(message);
    if (urlAliasErrorMessage) {
      setUrlCheckResult(false);
      setUrlCheckMessage(urlAliasErrorMessage);
    } else {
      setUrlCheckResult(true);
      setCheckingUrl(false);
      setUrlCheckMessage(ROOM_URL_ALIAS_VALID_MESSAGE);
    }

    return !urlAliasErrorMessage;
  }, []);

  const onCustomUrlKeyUp = useCallback(
    async (e: KeyboardEvent<HTMLInputElement>) => {
      e.stopPropagation();

      if (newRoomCustomUrl && e.key === 'Enter') {
        const isNewCustomUrlValid = checkUrl(newRoomCustomUrl);
        if (isNewCustomUrlValid) {
          dispatch(createRoom());
        }
      } else if (e.key !== 'Enter') {
        setUrlCheckResult(false);
        setCheckingUrl(true);
        setUrlCheckMessage('Checking URL...');
        clearTimeout(checkInputURLDelayTimer);
        checkInputURLDelayTimer = setTimeout(() => {
          checkUrl(newRoomCustomUrl);
        }, INPUT_URL_TIMER_DELAY_MS);
      }
    },
    [dispatch, newRoomCustomUrl, checkUrl]
  );

  const onCreateRoomClick = () => {
    dispatch(createRoom());
  };

  return (
    <Container>
      <InputContainer>
        <VerticalLabel htmlFor={newRoomNameInputId}>
          Room Name
          <TextInput id={newRoomNameInputId} type="text" value={newRoomName} autoFocus onChange={onNewRoomNameChange} />
        </VerticalLabel>
        {blankNewRoomName && <ErrorMessage>Your room name cannot be empty</ErrorMessage>}
        {newRoomNameExceedLength && <ErrorMessage>Your room name is too long!</ErrorMessage>}
      </InputContainer>

      <InputContainer>
        <div>
          <HorizontalLabel>
            Custom Room Link
            <SwitchContainer>
              <Icon className="toggle-url" alt="Link" src="/images/icons/link.svg" />
              <Icon className="toggle-url" alt="Pencil" src="/images/icons/pencil-white.svg" hidden />
              <HereSwitch toggleClass="toggle-url" sliderClass="new-name-toggle" onChange={onCustomUrlSwitchToggle} />
            </SwitchContainer>
          </HorizontalLabel>
        </div>

        <div>
          <HereInput
            toggleClass="toggle-url"
            prefix="here.fm/"
            placeholder="example"
            value={newRoomCustomUrl}
            checkResult={urlCheckResult}
            onChange={onCustomUrlChange}
            onKeyUp={onCustomUrlKeyUp}
            id="new-board-url"
            isHiddenOnDOM
            customInputStyles={{ textTransform: 'lowercase' }}
          />

          <Subtext className="toggle-url">A link will be auto-generated after creation</Subtext>
          {urlCheckMessage && (
            <UpdateUrlText className="toggle-url" isError={!urlCheckResult && !checkingUrl}>
              {urlCheckMessage}
            </UpdateUrlText>
          )}
        </div>
      </InputContainer>

      <InputContainer>
        <div>
          <HorizontalLabel>
            Lock Room
            <SwitchContainer>
              <Icon alt="Link" className="toggle-lock" src="/images/icons/unlock.svg" />
              <Icon alt="Pencil" className="toggle-lock" src="/images/icons/lock.svg" hidden />
              <HereSwitch onChange={onLockChange} toggleClass="toggle-lock" sliderClass="lock-toggle" />
            </SwitchContainer>
          </HorizontalLabel>
        </div>

        <div>
          <Subtext className="toggle-lock">Anyone with the link can access this room</Subtext>
          <Subtext className="toggle-lock" hidden>
            Users have to be let in by host to join
          </Subtext>
        </div>
      </InputContainer>

      <ButtonContainer>
        <CreateRoomButton type="button" onClick={onCreateRoomClick} disabled={!enableCreateRoomButton}>
          Create
        </CreateRoomButton>
      </ButtonContainer>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const InputContainer = styled.div`
  padding: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;

  & + & {
    margin-top: 10px;
    border-top: 1px solid #585858;
  }
`;

const Label = styled.label`
  display: flex;
  color: #ffffffee;
  text-align: center;
  font-size: 15px;
`;

const HorizontalLabel = styled(Label)`
  flex-direction: row;
  justify-content: space-between;
`;

const VerticalLabel = styled(Label)`
  flex-direction: column;
  align-items: flex-start;
`;

const TextInput = styled.input`
  padding: 8px;
  width: 100%;
  display: block;
  font-size: 18px;
  border: none;
  border-radius: 5px;

  &:focus {
    outline: 2px solid #6b1be3;
  }
`;

const ErrorMessage = styled.span`
  color: red;
`;

const SwitchContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Icon = styled.img`
  margin-right: 6px;
  width: 14px;
  height: 14px;
  border: 0px;
`;

const Subtext = styled.span`
  color: #ffffff99;
  font-size: 14px;
`;

const UpdateUrlText = styled.span<{ isError: boolean }>`
  margin-top: 12px;
  display: block;
  color: ${({ isError }) => (isError ? 'red' : 'white')};
  text-align: center;
`;

export default RoomCreationNaming;
